// src/shared/card-template.js
// Función pura — sin DOM, sin Firebase, sin side effects.
// Importada por: src/dashboard-profile-card.js y functions/index.js

// ============================================================
//  Escape HTML (obligatorio — data viene de usuario/Firestore)
// ============================================================

function esc(val) {
    return String(val ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

// ============================================================
//  Helpers puros
// ============================================================

function txt(val) {
    const s = String(val ?? '').trim();
    return s || '—';
}

function obtenerIniciales(nombre, fallback = 'RC') {
    const limpio = String(nombre ?? '').trim();
    if (!limpio) return fallback;
    const partes = limpio.split(/\s+/).filter(Boolean).slice(0, 2);
    if (!partes.length) return fallback;
    return partes.map(p => p[0]?.toUpperCase() || '').join('') || fallback;
}

function formatearTelefonoUY(valor) {
    const original = String(valor ?? '').trim();
    if (!original) return '';
    let digitos = original.replace(/\D/g, '');
    if (digitos.startsWith('598')) digitos = digitos.slice(3);
    if (digitos.length === 8) {
        return `+598 ${digitos.slice(0, 2)} ${digitos.slice(2, 5)} ${digitos.slice(5)}`;
    }
    return original.startsWith('+') ? original : `+${original}`;
}

function urlWhatsApp(valor) {
    const original = String(valor ?? '').trim();
    if (!original) return '';
    let digitos = original.replace(/\D/g, '');
    if (!digitos) return '';
    if (!digitos.startsWith('598') && digitos.length === 8) digitos = `598${digitos}`;
    return `https://wa.me/${digitos}`;
}

const GRID_REGEX = /^[A-Z]{2}[0-9]{2}[a-z]{2}$/;

function gridLink(grid) {
    const raw = String(grid ?? '').trim();
    if (raw.length !== 6) return esc(raw) || '—';
    const g = raw.slice(0, 2).toUpperCase() + raw.slice(2, 4) + raw.slice(4, 6).toLowerCase();
    if (!GRID_REGEX.test(g)) return esc(raw) || '—';
    return `<a href="https://www.karhukoti.com/maidenhead-grid-square-locator-desktop-map/?grid=${esc(g)}" target="_blank" rel="noopener noreferrer" class="profile-summary-card__grid-link">${esc(g)}</a>`;
}

function esUrlSegura(url) {
    const s = String(url ?? '').trim();
    if (!s) return false;
    try {
        const u = new URL(s);
        return u.protocol === 'https:' || u.protocol === 'http:';
    } catch {
        return false;
    }
}

// ============================================================
//  Modos de render de "Mejores posiciones"
// ============================================================

// completo  → dashboard privado, /u/:callsign, /embed/:callsign (acordeón <details>/<summary>)
// qrz       → /embed-live/:callsign (Cloud Function) y embeds estáticos generados en build (sin controles)
export const MODO_TARJETA_COMPLETO = 'completo';
export const MODO_TARJETA_QRZ = 'qrz';

// ============================================================
//  Componentes HTML
// ============================================================

function metaItem(label, value, extraClass = '', fieldKey = '') {
    const cls = `profile-summary-card__meta-item${extraClass ? ' ' + extraClass : ''}`;
    const fk = fieldKey ? ` data-field-key="${esc(fieldKey)}"` : '';
    return `<div class="${cls}"${fk}>
        <span class="profile-summary-card__meta-label">${esc(label)}</span>
        <span class="profile-summary-card__meta-value">${esc(txt(value))}</span>
    </div>`;
}

function metaItemHTML(label, inner, extraClass = '', fieldKey = '') {
    const cls = `profile-summary-card__meta-item${extraClass ? ' ' + extraClass : ''}`;
    const fk = fieldKey ? ` data-field-key="${esc(fieldKey)}"` : '';
    return `<div class="${cls}"${fk}>
        <span class="profile-summary-card__meta-label">${esc(label)}</span>
        <div class="profile-summary-card__meta-value">${inner || '—'}</div>
    </div>`;
}

function badge(texto, claseExtra = '') {
    return `<span class="profile-summary-card__badge${claseExtra ? ' ' + claseExtra : ''}">${esc(texto)}</span>`;
}

// Cantidad de concursos que se muestran siempre, en ambos modos, antes de truncar
const LIMITE_CONCURSOS_VISIBLES = 3;

function elegirPorCantidad(n, singular, plural) {
    return n === 1 ? singular : plural;
}

function conCantidad(plantilla, n) {
    return String(plantilla ?? '').replace('{n}', n);
}

function bestCard(titulo, data, claseExtra = '', modo = MODO_TARJETA_COMPLETO, L = DEFAULT_LABELS) {
    const puestoTxt = data?.puesto ? `${data.puesto}°` : '—';
    const lista = Array.isArray(data?.concursos) ? data.concursos : [];
    const total = lista.length;

    let concursosHTML;
    let totalHTML = '';

    if (!total) {
        concursosHTML = `<div class="profile-summary-card__best-concurso profile-summary-card__best-concurso--empty">—</div>`;
    } else {
        const visibles = lista.slice(0, LIMITE_CONCURSOS_VISIBLES);
        const resto = lista.slice(LIMITE_CONCURSOS_VISIBLES);
        const visiblesHTML = visibles.map(c => `<div class="profile-summary-card__best-concurso">${esc(c)}</div>`).join('');

        totalHTML = `<div class="profile-summary-card__best-total">${total} ${esc(elegirPorCantidad(total, L.totalConcursoUno, L.totalConcursoVarios))}</div>`;

        if (!resto.length) {
            concursosHTML = visiblesHTML;
        } else if (modo === MODO_TARJETA_QRZ) {
            const texto = conCantidad(elegirPorCantidad(resto.length, L.adicionalUno, L.adicionalVarios), resto.length);
            concursosHTML = `${visiblesHTML}<div class="profile-summary-card__best-concurso profile-summary-card__best-concurso--mas">${esc(texto)}</div>`;
        } else {
            const restoHTML = resto.map(c => `<div class="profile-summary-card__best-concurso">${esc(c)}</div>`).join('');
            const textoVerMas = conCantidad(elegirPorCantidad(resto.length, L.verMasUno, L.verMasVarios), resto.length);
            concursosHTML = `${visiblesHTML}<details class="profile-summary-card__best-details">
                <summary class="profile-summary-card__best-summary">
                    <span class="profile-summary-card__best-summary-mas">${esc(textoVerMas)}</span>
                    <span class="profile-summary-card__best-summary-menos">${esc(L.mostrarMenos)}</span>
                </summary>
                <div class="profile-summary-card__best-concurso-extra">${restoHTML}</div>
            </details>`;
        }
    }

    return `<div class="profile-summary-card__best-card${claseExtra ? ' ' + claseExtra : ''}">
        <div class="profile-summary-card__best-card-left">
            <div class="profile-summary-card__best-puesto">${esc(puestoTxt)}</div>
            <div class="profile-summary-card__best-title">${esc(titulo)}</div>
            <div class="profile-summary-card__best-ambito">${esc(data?.ambito || '—')}</div>
            ${totalHTML}
        </div>
        <div class="profile-summary-card__best-card-right">${concursosHTML}</div>
    </div>`;
}

function footerItem(label, value) {
    return `<div class="profile-summary-card__footer-item">
        <span class="profile-summary-card__footer-label">${esc(label)}</span>
        <span class="profile-summary-card__footer-value">${esc(txt(value))}</span>
    </div>`;
}

// ============================================================
//  Etiquetas por defecto (español)
// ============================================================

const DEFAULT_LABELS = {
    club:               'Club',
    paisDxcc:           'País / DXCC',
    zonaCQ:             'Zona CQ',
    zonaITU:            'Zona ITU',
    qth:                'QTH',
    grid:               'Grid',
    telefono:           'Teléfono',
    correo:             'Correo',
    enlaces:            'Enlaces',
    statsTitle:         'Resumen de actividad',
    statsSubtitle:      'Vista rápida del operador en la plataforma',
    concursos:          'Concursos',
    participaciones:    'Participaciones',
    mejoresPos:         'Mejores posiciones',
    mejorPais:          'Mejor país',
    mejorContinente:    'Mejor continente',
    mejorMundo:         'Mejor mundo',
    totalConcursoUno:   'concurso',
    totalConcursoVarios: 'concursos',
    verMasUno:          'Ver 1 concurso más',
    verMasVarios:       'Ver {n} concursos más',
    mostrarMenos:       'Mostrar menos',
    adicionalUno:       '+1 concurso adicional',
    adicionalVarios:    '+{n} concursos adicionales',
    modoMasUsado:       'Modo más usado:',
    concursoMasTrabajado: 'Concurso más trabajado:',
    ultimoConcurso:     'Último concurso:',
};

// ============================================================
//  Etiquetas en inglés
// ============================================================

const LABELS_EN = {
    club:                 'Club',
    paisDxcc:             'Country / DXCC',
    zonaCQ:               'CQ Zone',
    zonaITU:              'ITU Zone',
    qth:                  'QTH',
    grid:                 'Grid',
    telefono:             'Phone',
    correo:               'Email',
    enlaces:              'Links',
    statsTitle:           'Activity summary',
    statsSubtitle:        'Quick overview of the operator on the platform',
    concursos:            'Contests',
    participaciones:      'Participations',
    mejoresPos:           'Best positions',
    mejorPais:            'Best country',
    mejorContinente:      'Best continent',
    mejorMundo:           'Best worldwide',
    totalConcursoUno:     'contest',
    totalConcursoVarios:  'contests',
    verMasUno:            'Show 1 more contest',
    verMasVarios:         'Show {n} more contests',
    mostrarMenos:         'Show less',
    adicionalUno:         '+1 more contest',
    adicionalVarios:      '+{n} more contests',
    modoMasUsado:         'Most used mode:',
    concursoMasTrabajado: 'Most worked contest:',
    ultimoConcurso:       'Last contest:',
};

export function getLabelsForLang(lang) {
    return lang === 'en' ? LABELS_EN : DEFAULT_LABELS;
}

// ============================================================
//  Export principal
// ============================================================

export function renderizarTarjetaHTML(resumen, labels = DEFAULT_LABELS, opciones = {}) {
    const L = { ...DEFAULT_LABELS, ...labels };
    const modo = opciones?.modo === MODO_TARJETA_QRZ ? MODO_TARJETA_QRZ : MODO_TARJETA_COMPLETO;
    const id = resumen.identidad;
    const st = resumen.stats;

    const avatarHTML = id.avatar
        ? `<img class="profile-summary-card__avatar" src="${esc(id.avatar)}" alt="${esc(id.nombreVisible)}" loading="lazy">`
        : `<div class="profile-summary-card__avatar profile-summary-card__avatar--fallback">${esc(obtenerIniciales(id.indicativoPrincipal || id.nombreVisible))}</div>`;

    const badgesPrincipal = id.indicativoPrincipal
        ? badge(id.indicativoPrincipal, 'profile-summary-card__badge--primary')
        : '';
    const badgesEspeciales = Array.isArray(id.indicativosEspeciales)
        ? id.indicativosEspeciales.map(c => badge(c, 'profile-summary-card__badge--secondary')).join('')
        : '';

    const telefonoHTML = id.telefono
        ? `<a href="${esc(urlWhatsApp(id.telefono))}" target="_blank" rel="noopener noreferrer" class="profile-summary-card__whatsapp-link">${esc(formatearTelefonoUY(id.telefono))}</a>`
        : '';
    const emailHTML = id.correoElectronico
        ? `<a href="mailto:${esc(id.correoElectronico)}" class="profile-summary-card__email-pill">${esc(id.correoElectronico)}</a>`
        : '';

    const qrzHTML = esUrlSegura(id.qrzUrl)
        ? `<a href="${esc(id.qrzUrl)}" target="_blank" rel="noopener noreferrer" class="profile-summary-card__qrz-link" title="QRZ"><img src="/qrz.png" alt="QRZ" class="profile-summary-card__qrz-logo profile-summary-card__qrz-logo--dark"><img src="/qrz_black.png" alt="QRZ" class="profile-summary-card__qrz-logo profile-summary-card__qrz-logo--light"></a>`
        : '';
    const clubLogHTML = esUrlSegura(id.clubLogUrl)
        ? `<a href="${esc(id.clubLogUrl)}" target="_blank" rel="noopener noreferrer" class="profile-summary-card__qrz-link" title="ClubLog"><img src="/clublog.png" alt="ClubLog" class="profile-summary-card__qrz-logo"></a>`
        : '';
    const eQslHTML = esUrlSegura(id.eQslUrl)
        ? `<a href="${esc(id.eQslUrl)}" target="_blank" rel="noopener noreferrer" class="profile-summary-card__qrz-link" title="eQSL"><img src="/eQSLclaro.png" alt="eQSL" class="profile-summary-card__qrz-logo profile-summary-card__qrz-logo--dark"><img src="/eQSLoscuro.png" alt="eQSL" class="profile-summary-card__qrz-logo profile-summary-card__qrz-logo--light"></a>`
        : '';
    const redesHTML = Array.isArray(id.redes)
        ? id.redes
            .filter(r => r && esUrlSegura(r.url))
            .map(r => `<a href="${esc(r.url)}" target="_blank" rel="noopener noreferrer" class="profile-summary-card__link">${esc(r.nombre || 'Red')}</a>`)
            .join('')
        : '';
    const enlacesInner = (qrzHTML || clubLogHTML || eQslHTML || redesHTML)
        ? `<div class="profile-summary-card__links">${qrzHTML}${clubLogHTML}${eQslHTML}${redesHTML}</div>`
        : '';

    const modoFooter = st.modoMasUsado !== '—'
        ? `${st.modoMasUsado} (${st.modoMasUsadoCantidad})`
        : '—';
    const concursoFooter = st.concursoMasTrabajado !== '—'
        ? `${st.concursoMasTrabajado} (${st.concursoMasTrabajadoCantidad})`
        : '—';

    const _bestItems = [
        { label: L.mejorPais,       data: st.mejorPais,       cls: 'profile-summary-card__best-card--pais' },
        { label: L.mejorContinente, data: st.mejorContinente, cls: 'profile-summary-card__best-card--continente' },
        { label: L.mejorMundo,      data: st.mejorMundo,      cls: 'profile-summary-card__best-card--mundo' },
    ].filter(b => b.data?.puesto != null);
    const mejoresPosHTML = _bestItems.length
        ? `<div class="profile-summary-card__stats-subsection">
                <h4 class="profile-summary-card__stats-subsection-title">${esc(L.mejoresPos)}</h4>
            </div>
            <div class="profile-summary-card__best-grid">
                ${_bestItems.map(b => bestCard(b.label, b.data, b.cls, modo, L)).join('')}
            </div>`
        : '';

    return `<section class="profile-summary-card">
    <div class="profile-summary-card__inner">
        <div class="profile-summary-card__left">
            <div class="profile-summary-card__header">
                <div class="profile-summary-card__avatar-wrap">${avatarHTML}</div>
                <div class="profile-summary-card__identity">
                    <h2 class="profile-summary-card__name">${esc(id.nombreVisible)}</h2>
                    <div class="profile-summary-card__callsigns">${badgesPrincipal}${badgesEspeciales}</div>
                </div>
            </div>
            <div class="profile-summary-card__meta-grid">
                ${metaItem(L.club, id.club, '', 'club')}
                ${metaItem(L.paisDxcc, id.paisEntidad, '', 'pais')}
                ${metaItem(L.zonaCQ, id.zonaCQ, '', 'zona-cq')}
                ${metaItem(L.zonaITU, id.zonaITU, '', 'zona-itu')}
                ${metaItem(L.qth, id.qth, '', 'qth')}
                ${metaItemHTML(L.grid, gridLink(id.gridLocator), '', 'grid')}
            </div>
            <div class="profile-summary-card__contact-block">
                ${metaItemHTML(L.telefono, telefonoHTML, 'profile-summary-card__meta-item--telefono', 'telefono')}
                ${metaItemHTML(L.correo, emailHTML, 'profile-summary-card__meta-item--correo', 'correo')}
                ${metaItemHTML(L.enlaces, enlacesInner, 'profile-summary-card__meta-item--enlaces', 'enlaces')}
            </div>
        </div>
        <div class="profile-summary-card__right">
            <div class="profile-summary-card__stats-header">
                <h3 class="profile-summary-card__stats-title">${esc(L.statsTitle)}</h3>
                <p class="profile-summary-card__stats-subtitle">${esc(L.statsSubtitle)}</p>
            </div>
            <div class="profile-summary-card__top-stats">
                <div class="profile-summary-card__top-stat-box">
                    ${metaItem(L.concursos, st.concursos, 'profile-summary-card__top-stat-value')}
                </div>
                <div class="profile-summary-card__top-stat-box">
                    ${metaItem(L.participaciones, st.participaciones, 'profile-summary-card__top-stat-value')}
                </div>
            </div>
            ${mejoresPosHTML}
        </div>
    </div>
    <div class="profile-summary-card__footer">
        ${footerItem(L.modoMasUsado, modoFooter)}
        ${footerItem(L.concursoMasTrabajado, concursoFooter)}
        ${footerItem(L.ultimoConcurso, st.ultimoConcurso)}
    </div>
</section>`;
}
